"use client"

import { createContext, useContext, useState, useEffect, useRef, useCallback, ReactNode } from "react"
import { getAuthToken } from "@/lib/auth"
import { useAuth } from "@/components/auth-provider"
import { useContentEvents, CONTENT_EVENT, type ContentEventPayload } from "@/hooks/use-content-events"

// SSE リトライ間隔（ミリ秒）
const SSE_RETRY_INTERVAL = 15000

// SSE APIエンドポイント
const SSE_ENDPOINT = "/api/notification/update"

// 通知更新データの型
interface NotificationUpdate {
  notice: number
  task: number
  message: number
}

// Contextの型
interface NotificationContextType {
  /** 通知件数 */
  noticeCount: number
  /** タスク件数 */
  taskCount: number
  /** メッセージ件数 */
  messageCount: number
  /** 接続状態 */
  isConnected: boolean
  /** 通知件数を1減らす */
  decrementNoticeCount: () => void
  /** タスク件数を1減らす */
  decrementTaskCount: () => void
  /** メッセージ件数を1減らす */
  decrementMessageCount: () => void
  /** 内容更新イベントの発火カウンタ(種別ごと)。リスト再取得トリガに使う */
  contentTick: Record<string, number>
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined)

export const useNotification = () => {
  const context = useContext(NotificationContext)
  if (context === undefined) {
    throw new Error("useNotification must be used within a NotificationProvider")
  }
  return context
}

// 指定種別の内容更新イベント発火カウンタを返す。
// 値が変化したら、対応するリストを再取得する(useIdleGatedList/useIdleGate でゲート)用途に使う。
export const useContentTick = (type: string): number => {
  const { contentTick } = useNotification()
  return contentTick[type] ?? 0
}

interface NotificationProviderProps {
  children: ReactNode
}

export const NotificationProvider = ({ children }: NotificationProviderProps) => {
  const { isLoggedIn } = useAuth()
  const [noticeCount, setNoticeCount] = useState(0)
  const [taskCount, setTaskCount] = useState(0)
  const [messageCount, setMessageCount] = useState(0)
  const [isConnected, setIsConnected] = useState(false)
  const [contentTick, setContentTick] = useState<Record<string, number>>({})

  // cti_bridge の内容更新イベント受信時の処理
  // - リスト再取得トリガ(contentTick)を進める
  // - バッヂ件数は即時更新する(5秒ゲート対象外)。15秒ごとの通知SSEが最終的に整合を取る
  const handleContentEvent = useCallback((type: string, payload: ContentEventPayload | null) => {
    setContentTick((prev) => ({ ...prev, [type]: (prev[type] ?? 0) + 1 }))
    if (type === CONTENT_EVENT.chatMessage) {
      setMessageCount((prev) => prev + 1)
    } else if (payload?.resources?.includes("notification")) {
      setNoticeCount((prev) => prev + 1)
    }
  }, [])

  useContentEvents([CONTENT_EVENT.staff, CONTENT_EVENT.chatMessage], handleContentEvent)

  const eventSourceRef = useRef<EventSource | null>(null)
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // カウント減少関数
  const decrementNoticeCount = useCallback(() => {
    setNoticeCount((prev) => Math.max(0, prev - 1))
  }, [])

  const decrementTaskCount = useCallback(() => {
    setTaskCount((prev) => Math.max(0, prev - 1))
  }, [])

  const decrementMessageCount = useCallback(() => {
    setMessageCount((prev) => Math.max(0, prev - 1))
  }, [])

  // SSE接続を確立する関数
  const connect = useCallback(async () => {
    // 既存の接続があれば閉じる
    if (eventSourceRef.current) {
      eventSourceRef.current.close()
    }

    // リトライタイマーをクリア
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current)
      retryTimeoutRef.current = null
    }

    try {
      // 認証トークンを取得
      const token = await getAuthToken()
      if (!token) {
        // トークンがない場合はリトライ
        retryTimeoutRef.current = setTimeout(() => {
          connect()
        }, SSE_RETRY_INTERVAL)
        return
      }

      // トークンをクエリパラメータとして追加（EventSourceはカスタムヘッダーをサポートしないため）
      const url = `${SSE_ENDPOINT}?token=${encodeURIComponent(token)}`
      const eventSource = new EventSource(url)
      eventSourceRef.current = eventSource

      eventSource.onopen = () => {
        setIsConnected(true)
      }

      eventSource.onmessage = (event) => {
        try {
          const data: NotificationUpdate = JSON.parse(event.data)
          setNoticeCount(data.notice)
          setTaskCount(data.task)
          setMessageCount(data.message)
        } catch {
          // パースエラーは無視
        }
      }

      eventSource.onerror = () => {
        // 通信エラー時はエラーを表示せず、バッジの表示更新を止める
        setIsConnected(false)
        eventSource.close()
        eventSourceRef.current = null

        // リトライ間隔おきにアクセスを試みる
        retryTimeoutRef.current = setTimeout(() => {
          connect()
        }, SSE_RETRY_INTERVAL)
      }
    } catch {
      // EventSource作成自体に失敗した場合もリトライ
      setIsConnected(false)
      retryTimeoutRef.current = setTimeout(() => {
        connect()
      }, SSE_RETRY_INTERVAL)
    }
  }, [])

  // 認証完了後にSSE接続を開始
  useEffect(() => {
    if (!isLoggedIn) return

    connect()

    // クリーンアップ
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close()
        eventSourceRef.current = null
      }
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current)
        retryTimeoutRef.current = null
      }
    }
  }, [connect, isLoggedIn])

  return (
    <NotificationContext.Provider
      value={{
        noticeCount,
        taskCount,
        messageCount,
        isConnected,
        decrementNoticeCount,
        decrementTaskCount,
        decrementMessageCount,
        contentTick,
      }}
    >
      {children}
    </NotificationContext.Provider>
  )
}
